import { useMemo } from 'react';
import { ArtistSubscriptionFragment, PlanTermEnum } from '@minecraft.graphql-operations';
import { gqlSubscription } from '@minecraft.graphql-types';
import { PlanType } from '../../usePlans';
import { useTranslation } from '../../../I18N';
import { priceFormatter } from '../../helpers/priceFormatter';

export const useDiscountHook = ({ plans }: { plans?: PlanType[] }) => {
  const { t } = useTranslation();
  const discountedPlan = plans?.find((plan) => Boolean(plan?.discountPercent && plan?.discountType));

  const originalPrice = priceFormatter({
    price: Number(discountedPlan?.strikeThroughPrice),
    t,
    roundingFloor: true,
  });
  const annualPrice = priceFormatter({
    price: Number(discountedPlan?.discountedPrice),
    currencyCode: discountedPlan?.currencyCode,
    t,
    roundingFloor: true,
  });
  const price = priceFormatter({
    price:
      discountedPlan?.term?.code === PlanTermEnum.Annual
        ? Number(discountedPlan?.discountedPrice) / 12
        : Number(discountedPlan?.discountedPrice),
    currencyCode: discountedPlan?.currencyCode,
    t,
    roundingFloor: true,
  });

  const billedText = useMemo(() => {
    if (discountedPlan?.totalBillingCycles)
      return t('common:paywall.plan.billed_text_with_billing_cycles', {
        months: discountedPlan.totalBillingCycles,
        price: originalPrice,
      });
    if (discountedPlan?.term.code === PlanTermEnum.Annual)
      return `${annualPrice} ${t('common:paywall.plan.billed_yearly')}`;
    return '';
  }, [discountedPlan, t, originalPrice, annualPrice]);

  return useMemo(
    () => ({
      originalPrice,
      price,
      annualPrice,
      billedText,
      discountedPlan,
      discountType: discountedPlan?.discountType,
    }),
    [originalPrice, price, billedText, discountedPlan, annualPrice]
  );
};

export type DiscountModalProps = {
  onCTAClick: () => void;
  subscription?: ArtistSubscriptionFragment | gqlSubscription;
} & Omit<ReturnType<typeof useDiscountHook>, 'discountType'>;
